import { useState, useEffect, useCallback } from "react";

/**
 * Uses the version.json file generated during build to detect new available versions
 * and handle application reload.
 */
const useVersionCheck = () => {
  const [state, setState] = useState({
    isNewVersionAvailable: false,
    currentVersion: null,
    newVersion: null,
    isChecking: false,
  });

  // Constants
  const VERSION_STORAGE_KEY = "herno_b2b_app_version";
  const VERSION_ENDPOINT = "/version.json";
  const FETCH_TIMEOUT = 5000; // 5 seconds

  /**
   * Retrieve version saved in localStorage
   */
  const getStoredVersion = () => {
    try {
      return localStorage.getItem(VERSION_STORAGE_KEY) || null;
    } catch (error) {
      console.warn("Error reading localStorage:", error);
      return null;
    }
  };

  /**
   * Save version in localStorage
   */
  const setStoredVersion = (version) => {
    try {
      localStorage.setItem(VERSION_STORAGE_KEY, version);
      // console.log("Version saved in localStorage:", version);
    } catch (error) {
      console.warn("Error saving to localStorage:", error);
    }
  };

  /**
   * Parse and validate version response
   */
  const parseVersionResponse = async (response) => {
    // Check if response is actually JSON
    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      throw new Error(
        "version.json file not found - server returned HTML instead of JSON",
      );
    }

    try {
      const versionData = await response.json();

      if (!versionData || !versionData.version) {
        throw new Error("Missing 'version' field in response");
      }

      return versionData;
    } catch (parseError) {
      if (parseError.message.includes('Missing "version" field')) {
        throw parseError;
      }
      throw new Error("version.json file not found or contains invalid JSON");
    }
  };

  /**
   * Fetch version.json with cache busting and timeout
   */
  const fetchRemoteVersion = useCallback(async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

    try {
      const timestamp = Date.now();
      const url = `${VERSION_ENDPOINT}?t=${timestamp}`; // Cache busting with timestamp to prevent browser from using cached version of the file

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const versionData = await parseVersionResponse(response);

      // console.log("Remote version data:", versionData);
      return versionData;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error.name === "AbortError") {
        throw new Error("Timeout - version.json request too slow");
      }

      throw error;
    }
  }, [VERSION_ENDPOINT, FETCH_TIMEOUT]);

  /**
   * Compare versions and determine if update is necessary
   */
  const compareVersions = (storedVersion, remoteVersion) => {
    return storedVersion !== remoteVersion;
  };

  const reloadApplication = (newVersion) => {
    // Save new version before reload
    setStoredVersion(newVersion);
    window.location.reload();
  };

  /**
   * Check if new version is available
   */
  const checkVersion = useCallback(async () => {
    if (state.isChecking) {
      return;
    }

    setState((prev) => ({
      ...prev,
      isChecking: true,
    }));

    // Initialize result variables
    let currentVersion = null;
    let newVersion = null;
    let isNewVersionAvailable = false;
    let error = null;
    const lastChecked = Date.now();

    try {
      // console.log("Starting version check...");

      // Fetch remote version
      const remoteVersionData = await fetchRemoteVersion();
      const remoteVersionString = remoteVersionData.version;

      // Get stored version
      const storedVersion = getStoredVersion();

      // console.log("Comparing versions:", {
      //   stored: storedVersion,
      //   remote: remoteVersionString,
      // });

      // First installation
      if (!storedVersion) {
        // console.log("First installation, saving current version");
        setStoredVersion(remoteVersionString);

        currentVersion = remoteVersionString;
        newVersion = null;
        isNewVersionAvailable = false;
      } else {
        // Check if update is needed
        const needsUpdate = compareVersions(storedVersion, remoteVersionString);

        if (needsUpdate) {
          // console.log("New version available!", {
          //   from: storedVersion,
          //   to: remoteVersionString,
          // });

          // Update localStorage immediately with the new version
          // This prevents showing the modal again on manual browser refresh
          setStoredVersion(remoteVersionString);

          // Keep the original version for UI display (shows "from X to Y")
          currentVersion = storedVersion;
          newVersion = remoteVersionString;
          isNewVersionAvailable = true;
        } else {
          // console.log("Version up to date, no update necessary");

          currentVersion = storedVersion;
          newVersion = remoteVersionString;
          isNewVersionAvailable = false;
        }
      }
    } catch (err) {
      console.warn("Version check error:", err.message);
      error = err.message;
    }

    setState((prev) => ({
      ...prev,
      currentVersion,
      newVersion,
      isNewVersionAvailable,
      error,
      isChecking: false,
      lastChecked,
    }));
  }, [state.isChecking, fetchRemoteVersion]);

  /**
   * Force application update
   */
  const updateVersion = useCallback(() => {
    if (!state.newVersion) {
      console.warn("No new version available for update");
      return;
    }

    reloadApplication(state.newVersion);
  }, [state.newVersion]);

  // Auto-check on startup
  useEffect(() => {
    // console.log("Hook useVersionCheck initialized");

    // Initial delay to not block startup
    const timer = setTimeout(() => {
      checkVersion();
    }, 2000); // 2 seconds after mount

    return () => clearTimeout(timer);
  }, []); // Only on mount

  return {
    isNewVersionAvailable: state.isNewVersionAvailable,
    currentVersion: state.currentVersion,
    newVersion: state.newVersion,
    isChecking: state.isChecking,

    checkVersion,
    updateVersion,
  };
};

export default useVersionCheck;
